import { getToken } from "firebase/messaging";
import {
  getFirebaseMessaging,
  isFirebaseConfigured,
  vapidKey,
} from "~/lib/firebase";
import { getClientInfo } from "~/lib/userAgentParser";

const DEVICE_TOKEN_KEY = "notif_fcm_device_token";
const PUSH_PREF_KEY = "notif_push_enabled";

export const setPushEnabledPref = (enabled: boolean) => {
  try {
    localStorage.setItem(PUSH_PREF_KEY, enabled ? "true" : "false");
  } catch (err) {
    // ignore localStorage errors
  }
};

export const isPushEnabledPref = (): boolean => {
  try {
    const pref = localStorage.getItem(PUSH_PREF_KEY);
    return pref !== "false";
  } catch (err) {
    return true;
  }
};

export const isPushSupported = (): boolean => {
  if (!isFirebaseConfigured()) return false;
  if (typeof window === "undefined" || !("Notification" in window))
    return false;
  if (!("serviceWorker" in navigator)) return false;
  return true;
};

export const isPermissionDenied = (): boolean =>
  Notification.permission === "denied";

/**
 * Returns a human-readable device name and type parsed from the browser User-Agent.
 */
export const getDeviceInfo = (): {
  deviceName: string;
  deviceType: string;
  os: string;
  browser: string;
  device: string;
} => {
  const { os, browser, device } = getClientInfo();
  return {
    os,
    browser,
    device,
    deviceName: `${browser} on ${os}`,
    deviceType: device === "Mobile" ? "Web - Mobile" : "Web - Desktop",
  };
};

export const registerServiceWorker =
  async (): Promise<ServiceWorkerRegistration> => {
    try {
      const existing = await navigator.serviceWorker.getRegistrations();
      //find existing registration for our Firebase messaging service worker and get all
      //registrations, we need to unregister old ones if more than one is found to avoid duplicate notifications

      const existingMessagingSWs = existing.filter((r) =>
        r.active?.scriptURL.includes("firebase-messaging-sw.js"),
      );
      if (existingMessagingSWs.length > 1) {
        console.warn(
          `[notifications.service] Multiple existing messaging service workers found (${existingMessagingSWs.length}), unregistering all to avoid duplicates`,
        );
        await Promise.all(existingMessagingSWs.map((r) => r.unregister()));
      } else if (existingMessagingSWs.length === 1) {
        return existingMessagingSWs[0];
      }

      console.log(
        "[notifications.service] Registering new service worker for Firebase messaging",
      );
      return navigator.serviceWorker.register("/firebase-messaging-sw.js");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[notifications.service] registerServiceWorker failed", {
        message,
        err,
      });
      throw err;
    }
  };

/**
 * Request push notification permission and return the FCM token.
 * Returns null if permission was denied or Firebase is not configured.
 */
export const requestPermissionAndGetToken = async (): Promise<
  string | null
> => {
  try {
    console.debug("[notifications.service] requestPermissionAndGetToken start");
    const permission = await Notification.requestPermission();
    console.debug(`[notifications.service] permission ${permission}`);
    if (permission !== "granted") return null;

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      console.debug(
        "[notifications.service] getFirebaseMessaging returned null",
      );
      return null;
    }

    const registration = await registerServiceWorker();
    console.debug(
      "[notifications.service] service worker registration obtained",
      {
        scriptURL:
          registration.active?.scriptURL ??
          registration.installing?.scriptURL ??
          "pending",
      },
    );

    const token = await getToken(messaging, {
      vapidKey: vapidKey ?? undefined,
      serviceWorkerRegistration: registration,
    });

    console.debug("[notifications.service] getToken returned", { token });

    if (token) {
      try {
        localStorage.setItem(DEVICE_TOKEN_KEY, token);
      } catch (err) {
        console.warn(
          "[notifications.service] failed to persist token to localStorage",
          err,
        );
      }
    }

    return token ?? null;
  } catch (err) {
    console.error(
      "[notifications.service] requestPermissionAndGetToken failed",
      err,
    );
    return null;
  }
};

/**
 * Silently retrieve the current FCM token without requesting permission.
 * Returns null if permission is not granted or Firebase is not configured.
 * Also updates the stored token in localStorage.
 */
export const getTokenSilently = async (): Promise<string | null> => {
  if (typeof window === "undefined") return null;
  if (!isPushSupported()) return null;
  if (Notification.permission !== "granted") return null;

  const messaging = await getFirebaseMessaging();
  if (!messaging) return null;

  try {
    const registration = await registerServiceWorker();
    const token = await getToken(messaging, {
      vapidKey: vapidKey ?? undefined,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      localStorage.setItem(DEVICE_TOKEN_KEY, token);
    }

    return token ?? null;
  } catch {
    return null;
  }
};

/**
 * Retrieve the last known device token from localStorage.
 * Used when permission may have been revoked and getToken() can't be called.
 */
export const getStoredToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(DEVICE_TOKEN_KEY);
};

export const disablePush = async (): Promise<string | null> => {
  // Try to get the current token to unregister it server-side.
  const token = (await getTokenSilently()) ?? getStoredToken();
  if (token) {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  }
  return token;
};

const api = {
  isPushSupported,
  isPushEnabledPref,
  setPushEnabledPref,
  isPermissionDenied,
  getDeviceInfo,
  registerServiceWorker,
  requestPermissionAndGetToken,
  getTokenSilently,
  getStoredToken,
  disablePush,
};

export default api;
